import React from 'react';
import { Info, AlertTriangle, AlertCircle, X, Check } from 'lucide-react';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { notificationSourceLabel } from '../../utils/notificationSourceLabel';
import type { NotificationOut, NotificationSeverity } from '../../types/notification.types';
import './NotificationItem.css';

export interface NotificationItemProps {
  notification: NotificationOut;
  onClick: () => void;
  canDismiss: boolean;
  onDismiss?: (e: React.MouseEvent) => void;
  layout?: 'dropdown' | 'page';
  isSelected?: boolean;
  isChecked?: boolean;
  onCheckChange?: (checked: boolean) => void;
}

function SeverityIcon({ severity }: { severity: NotificationSeverity }) {
  if (severity === 'warning') return <AlertTriangle size={16} />;
  if (severity === 'error') return <AlertCircle size={16} />;
  return <Info size={16} />;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  canDismiss,
  onDismiss,
  layout = 'dropdown',
  isSelected = false,
  isChecked = false,
  onCheckChange,
}) => {
  const { severity, title, body, source_service, created_at, is_read } = notification;

  const isUnread = !is_read;

  const itemClasses = [
    'nd-item',
    isUnread ? 'is-unread' : '',
    layout === 'page' ? 'nd-item--page' : '',
    isSelected ? 'is-selected' : '',
  ].filter(Boolean).join(' ');

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.nd-item-dismiss') || target.closest('.nd-item-check')) return;
    onClick();
  };

  const handleCheckClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCheckChange?.(!isChecked);
  };

  return (
    <div className={itemClasses} onClick={handleClick} role="listitem">
      {layout === 'page' && onCheckChange !== undefined && (
        <div
          className={`nd-item-check${isChecked ? ' is-checked' : ''}`}
          onClick={handleCheckClick}
          role="checkbox"
          aria-checked={isChecked}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              onCheckChange?.(!isChecked);
            }
          }}
        >
          {isChecked && <Check size={12} />}
        </div>
      )}

      <div className={`nd-item-icon severity-${severity}`}>
        <SeverityIcon severity={severity} />
      </div>

      <div className="nd-item-body">
        <div className="nd-item-title">{title}</div>
        <div className="nd-item-desc">{body}</div>
        <div className="nd-item-meta">
          <span className="nd-item-source">{notificationSourceLabel(source_service)}</span>
          {layout === 'dropdown' && (
            <span className="nd-item-time">{formatRelativeTime(created_at)}</span>
          )}
        </div>
      </div>

      {layout === 'dropdown' && canDismiss && onDismiss && (
        <button
          type="button"
          className="nd-item-dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(e);
          }}
          aria-label="Скрыть уведомление"
          title="Скрыть"
        >
          <X size={14} />
        </button>
      )}

      {layout === 'page' && (
        <div className="nd-item-right">
          <div className="nd-item-right-time">{formatRelativeTime(created_at)}</div>
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
